import { IconTrash } from "@tabler/icons-react";
import { Button, Flex, Form, Modal, Skeleton, Typography } from "antd";
import { useMemo } from "react";
import { useOrgUsers } from "@/common/artemis-operations/members";
import {
	useAddOrgAdmin,
	useOrgAdmins,
	useRemoveOrgAdmin,
} from "@/common/artemis-operations/organization";
import { confirmDestructive } from "@/common/confirm";
import {
	DELETE_RESOURCE_ERROR_FAILED,
	DELETE_RESOURCE_INFO_COMPLETED,
} from "@/common/messages";
import type { OrgUser } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { BaseContainer, CustomIcon, QueryGuard } from "@/components/base";
import { CustomList } from "@/components/custom-list";
import { UserSearchSelect } from "@/components/users/user-search-select";

function AddAdminModal({
	open,
	onClose,
	existingAdminUserIds,
}: {
	open: boolean;
	onClose: () => void;
	existingAdminUserIds: Set<string>;
}) {
	const [form] = Form.useForm();
	const notify = useNotify();
	const addAdmin = useAddOrgAdmin();

	function handleClose() {
		form.resetFields();
		onClose();
	}

	function handleOk() {
		form.validateFields().then((values) => {
			addAdmin.mutate(values.userId, {
				onSuccess: () => {
					notify.success("Admin added.");
					handleClose();
				},
				onError: (err) =>
					notify.error((err as Error).message || "Failed to add admin."),
			});
		});
	}

	return (
		<Modal
			title="Add organization admin"
			open={open}
			onCancel={handleClose}
			onOk={handleOk}
			okText="Add"
			confirmLoading={addAdmin.isPending}
			destroyOnHidden
		>
			<Form form={form} layout="vertical" style={{ marginTop: 16 }}>
				<Form.Item
					name="userId"
					label="User"
					rules={[{ required: true, message: "Select a user" }]}
				>
					<UserSearchSelect excludeIds={existingAdminUserIds} />
				</Form.Item>
			</Form>
		</Modal>
	);
}

function AdminItem({
	userId,
	user,
	isLast,
}: {
	userId: string;
	user: OrgUser | undefined;
	isLast: boolean;
}) {
	const notify = useNotify();
	const removeAdmin = useRemoveOrgAdmin();

	function handleRemove() {
		confirmDestructive({
			title: "Remove admin",
			content: `Remove ${user?.displayName ?? user?.email ?? userId} as organization admin?`,
			onOk() {
				removeAdmin.mutate(userId, {
					onSuccess: () => notify.success(DELETE_RESOURCE_INFO_COMPLETED),
					onError: (err) =>
						notify.error(
							(err as Error).message || DELETE_RESOURCE_ERROR_FAILED,
						),
				});
			},
		});
	}

	return (
		<Flex
			justify="space-between"
			align="center"
			gap={8}
			style={{ width: "100%" }}
		>
			<Flex vertical style={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
				<Typography.Text
					strong
					ellipsis={{ tooltip: user?.displayName ?? user?.email ?? userId }}
				>
					{user?.displayName || user?.email || userId}
				</Typography.Text>
				{user?.email && (
					<Typography.Text type="secondary" ellipsis={{ tooltip: user.email }}>
						{user.email}
					</Typography.Text>
				)}
			</Flex>
			<Button
				icon={<CustomIcon icon={IconTrash} />}
				type="text"
				danger
				onClick={handleRemove}
				loading={removeAdmin.isPending}
				disabled={isLast}
				title={isLast ? "Cannot remove the last organization admin" : undefined}
			/>
		</Flex>
	);
}

/** Lists organization admins with add/remove controls. Removing the last admin is disabled.
 * The Add action is owned by the page-level action bar (controlled via addOpen/onCloseAdd). */
export function OrganizationAdmins({
	addOpen,
	onCloseAdd,
}: {
	addOpen: boolean;
	onCloseAdd: () => void;
}) {
	const admins = useOrgAdmins();
	const orgUsers = useOrgUsers();

	const userMap = useMemo(() => {
		const map = new Map<string, OrgUser>();
		for (const u of orgUsers.data ?? []) map.set(u.id, u);
		return map;
	}, [orgUsers.data]);

	const existingAdminUserIds = useMemo(
		() => new Set((admins.data ?? []).map((a) => a.userId)),
		[admins.data],
	);

	const isLast = (admins.data?.length ?? 0) <= 1;

	return (
		<BaseContainer title="Admins">
			<QueryGuard
				isPending={admins.isPending || orgUsers.isPending}
				isError={admins.isError || orgUsers.isError}
				isNull={false}
				pending={<Skeleton active />}
			>
				<CustomList
					data={admins.data ?? []}
					renderItem={(admin) => (
						<AdminItem
							userId={admin.userId}
							user={userMap.get(admin.userId)}
							isLast={isLast}
						/>
					)}
				/>
			</QueryGuard>

			<AddAdminModal
				open={addOpen}
				onClose={onCloseAdd}
				existingAdminUserIds={existingAdminUserIds}
			/>
		</BaseContainer>
	);
}
